//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import angular, {
  ILogService,
  IQService,
  IRootScopeService,
  ITimeoutService,
  IWindowService,
} from 'angular';
import _ from 'underscore';

import 'app/bulk-actions/bulk-actions-api-service';
import { clearItems } from 'app/bulk-actions/bulk-actions-actions/action-creators';
import * as bulkActionsApi from '@apis/bulkActionsApi';
import { getStateName } from '@react-bridge/wrappers/navigation';
import { getRightsRequestStatuses } from '@apis/gatewayMediaApi';
import { IRangeSelectedItem } from '@components/organisms/content-card-grid/ContentCardRangeSelectContext';
import * as fanreelsApi from '@fanreels/fanreelsApi';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ICuralateStore } from '@models/AppState';
import ClientId from '@models/ClientId';
import Cuid from '@models/Cuid';
import { IRightsRequestState } from '@models/IItemMetadata';
import PartialCuid from '@models/PartialCuid';
import { actions as shareDataActions } from '@share/actions/shareDataActions';
import getSharedItemsEligibleToRemove from '@share/selectors/getSharedItemsEligibleToRemove';
import getSharedItemsEligibleToRequestReviewNow from '@share/selectors/getSharedItemsEligibleToRequestReviewNow';
import { taggingFeedActions } from '@tagging/actions/taggingFeedActions';
import { createAction } from '@utils/actions';
import { debounce } from '@utils/debounce';
import { Normalized } from '@utils/normalize';
import { ICuralateSession } from 'app/basic-session/ICuralateSession';
import { BulkActionsApiService } from 'app/bulk-actions/bulk-actions-api-service';
import { BULK_ACTIONS_ACTIONS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';
import { IBulkActionsState } from 'app/bulk-actions/model/AppState';
import { ISelectedItem } from 'app/bulk-actions/model/ISelectedItem';
import { ReduxDispatch } from 'app/curalate.store';
import gaApi from 'app/initialization/ga/ga-api';
import { getContentLibraryItems } from '@apis/content-library/contentLibraryApi';
import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { getMediaTrackingHandler } from '@tracker/mediaTracking/mediaTrackingHandler';
import {
  MediaTrackingEventName,
  MediaTrackingSource,
} from '@tracker/mediaTracking/mediaTrackingModel';
import { getItemsMetadata } from '@apis/bulkActionsApi';

import UniversalImageActionsApiService from '@legacy-components/common/universal-image-actions/universal-image-actions-api-service';
import { isDefined } from '@utils/filterPredicates';
import {
  processCaptioningOnSelectedItems,
  getCaptionStatuses,
} from '@apis/video-processing/videoProcessingApi';
import { CAPTIONS_STATUS_VALUE } from 'app/constants/mute-tooltip-content';
import {
  captionFailedToast,
  captionSuccessToast,
  captionMaxTryFailedToast,
  captionInvalidCuidToast,
  captionBulkRequestToast,
  cpationSelectionLimitToast,
  imagesSelectionFailedToast,
} from 'app/bulk-actions/bulk-actions-container/bulk-actions-toasts';
import { queryClient } from '@queries/ReactQueryProvider';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { CasProductId } from '@apis/campaigns/contentSourceApiModel';

function selectCuidItem(newItem: ISelectedItem) {
  return createAction({
    payload: newItem,
    type: BULK_ACTIONS_ACTIONS.SELECT_ITEM,
  });
}

function selectCuidItems(newItems: ISelectedItem[]) {
  return createAction({
    newItems,
    type: BULK_ACTIONS_ACTIONS.SELECT_ITEMS,
  });
}

function unselectCuidItem(removedItem: string) {
  return createAction({
    payload: removedItem,
    type: BULK_ACTIONS_ACTIONS.UNSELECT_ITEM,
  });
}

function unselectCuidItems(removedCuids: string[]) {
  return createAction({
    removedCuids,
    type: BULK_ACTIONS_ACTIONS.UNSELECT_ITEMS,
  });
}

function openBulkAddLabelsModal() {
  return createAction({
    type: BULK_ACTIONS_ACTIONS.OPEN_BULK_ADD_LABELS_MODAL,
  });
}

export function closeBulkAddLabelsModal() {
  return createAction({
    type: BULK_ACTIONS_ACTIONS.CLOSE_BULK_ADD_LABELS_MODAL,
  });
}

function openBulkRemoveLabelsModal() {
  return createAction({
    type: BULK_ACTIONS_ACTIONS.OPEN_BULK_REMOVE_LABELS_MODAL,
  });
}

export function setItemsBulkAddLabelsModal(mediaItems: IContentLibraryItem[]) {
  return createAction({
    mediaItems: mediaItems,
    type: BULK_ACTIONS_ACTIONS.SET_ITEMS_BULK_ADD_LABELS_MODAL,
  });
}

function openConfirmModal(action: string) {
  return createAction({
    payload: action,
    type: BULK_ACTIONS_ACTIONS.OPEN_ACTION_CONFIRMATION_MODAL,
  });
}

export function closeConfirmModal() {
  return createAction({
    type: BULK_ACTIONS_ACTIONS.CLOSE_ACTION_CONFIRMATION_MODAL,
  });
}

function enableAction(action: string) {
  return createAction({
    payload: action,
    type: BULK_ACTIONS_ACTIONS.ENABLE_BULK_ACTION,
  });
}

function disableAction(action: string) {
  return createAction({
    payload: action,
    type: BULK_ACTIONS_ACTIONS.DISABLE_BULK_ACTION,
  });
}

function setCollection(id: string, name: string) {
  return createAction({
    payload: {
      id,
      name,
    },
    type: BULK_ACTIONS_ACTIONS.SET_CURRENT_COLLECTION,
  });
}

function clearCollection() {
  return createAction({
    type: BULK_ACTIONS_ACTIONS.CLEAR_CURRENT_COLLECTION,
  });
}

function setFeatures(featuresMap: any) {
  return createAction({
    payload: featuresMap,
    type: BULK_ACTIONS_ACTIONS.SET_FEATURES,
  });
}

function computeIsDownloadDisabled(uiaState: IBulkActionsState) {
  const maxDownloadItems = 1000;

  if (uiaState.selectedItems.length > maxDownloadItems) {
    return `You cannot download over ${maxDownloadItems} items`;
  }

  return false;
}

function setItemDetails(cuid: PartialCuid, itemDetails: any) {
  return createAction({
    payload: {
      cuid,
      itemDetails,
    },
    type: BULK_ACTIONS_ACTIONS.SET_ITEM_DETAILS,
  });
}

function loadGlobalData() {
  return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
    const isSyndicationClient = getState().session.client.isSyndication;

    const featuresMap = {
      isCollectionsEnabled: !isSyndicationClient,
      isLabelsEnabled: !isSyndicationClient,
      isLibraryEnabled: !isSyndicationClient,
    };

    dispatch(setFeatures(featuresMap));
  };
}

function receiveRightsStatuses(
  rightsMap: Record<PartialCuid, IRightsRequestState>,
) {
  return createAction({
    rightsMap,
    type: BULK_ACTIONS_ACTIONS.RECEIVE_RIGHTS_STATUSES,
  });
}

function receiveSelectedItemsForCaptions(mediaItems: string[]) {
  return createAction({
    mediaItems,
    type: BULK_ACTIONS_ACTIONS.SET_SELECTED_ITEMS_FOR_CAPTIONS,
  });
}
function clearSelectedItemsForCaptions() {
  return createAction({
    type: BULK_ACTIONS_ACTIONS.CLEAR_SELECTED_ITEMS_FOR_CAPTIONS,
  });
}

function setIsVideoActionInProgress(isVideoActionInProgress: boolean) {
  return createAction({
    isVideoActionInProgress,
    type: BULK_ACTIONS_ACTIONS.SET_IS_VIDEO_ACTION_IN_PROGRESS,
  });
}

function setCaptionTurnOnOffState(captionTurnOnOffState: string) {
  return createAction({
    captionTurnOnOffState,
    type: BULK_ACTIONS_ACTIONS.SET_CAPTION_TURN_ON_OFF_STATE,
  });
}

function setSelectedItemsForContentSource(items: CasProductId[]) {
  return createAction({
    items,
    type: BULK_ACTIONS_ACTIONS.SET_SELECTED_ITEMS_FOR_CONTENT_SOURCE,
  });
}

export type BulkActionsAction = ReturnType<
  | typeof selectCuidItem
  | typeof selectCuidItems
  | typeof unselectCuidItem
  | typeof unselectCuidItems
  | typeof clearItems
  | typeof openConfirmModal
  | typeof closeConfirmModal
  | typeof openBulkAddLabelsModal
  | typeof openBulkRemoveLabelsModal
  | typeof closeBulkAddLabelsModal
  | typeof setItemsBulkAddLabelsModal
  | typeof enableAction
  | typeof disableAction
  | typeof setCollection
  | typeof clearCollection
  | typeof setFeatures
  | typeof setItemDetails
  | typeof receiveRightsStatuses
  | typeof receiveSelectedItemsForCaptions
  | typeof clearSelectedItemsForCaptions
  | typeof setCaptionTurnOnOffState
  | typeof setIsVideoActionInProgress
  | typeof setSelectedItemsForContentSource
>;

export type IBulkActionsActions = ReturnType<typeof BulkActionsActions>;

function BulkActionsActions(
  $log: ILogService,
  $q: IQService,
  $rootScope: IRootScopeService,
  $timeout: ITimeoutService,
  $window: IWindowService,
  bulkActionsApiService: BulkActionsApiService,
  contentLibraryItemDetailsService,
  ErrorNotificationService,
  UniversalImageActionsService,
  UniversalImageActionsApiService: UniversalImageActionsApiService,
  UniversalImageActionsView,
) {
  function trackBulkSelectedMediaItems(
    selectedItems: ISelectedItem[],
    event: MediaTrackingEventName,
  ) {
    selectedItems
      .filter((item): item is Required<ISelectedItem> => isDefined(item.source))
      .forEach((item) => {
        const mediaTrackingHandler = getMediaTrackingHandler(item.source);
        UniversalImageActionsApiService.getItemMetadataAsync(item.cuid).then(
          ({ fileId, rootCuid, videoServiceId }) => {
            mediaTrackingHandler.track({
              cuid: rootCuid,
              event,
              fileId: fileId,
              videoId: videoServiceId,
            });
          },
        );
      });
  }

  function openUia(
    itemId: string,
    itemType: string,
    page?: string,
    context?: any,
  ) {
    return (dispatch, getState: () => ICuralateStore) => {
      const session = getState().session as ICuralateSession;

      gaApi.recordEvent(gaApi.CATEGORY_BULK_UIA, gaApi.ACTION_BULK_UIA_OPEN);

      const launchOptions = {
        itemId,
        itemType,
        page,
      };

      if (context && context.additionalLaunchOptions) {
        _(launchOptions).extend(context.additionalLaunchOptions);
      }

      if (session.client.isShareV2Vendor) {
        const share = getState().share.data;
        const isAddLabelsEnabled =
          !!share?.publisherClients?.[0]?.isCopyCreatorLabelsEnabled;
        _(launchOptions).extend({ isAddLabelsEnabled });
      }

      UniversalImageActionsService.launch(
        UniversalImageActionsView.Details.name,
        launchOptions,
      );
    };
  }

  function removeFromLibrary() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_REMOVE_FROM_LIBRARY,
      );

      dispatch(closeConfirmModal());
      const currentCuids = getState().bulkActions.selectedItems.map(
        (item) => item.cuid,
      );
      dispatch(clearItems());

      bulkActionsApiService
        .removeFromLibraryAsync(currentCuids)
        .then((response) => {
          dispatch(
            showActionSuccessToast(response, 'Removed items from your library'),
          );
          $rootScope.$broadcast('uia:remove-items-from-library', {
            items: currentCuids,
          });
        })
        .catch((error) => {
          showActionErrorDialog(error);
        });
    };
  }

  function addToLibrary() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_ADD_TO_LIBRARY,
      );

      dispatch(closeConfirmModal());

      const selectedItems = getState().bulkActions.selectedItems;
      const currentCuids = selectedItems.map((item) => item.cuid);
      dispatch(clearItems());

      trackBulkSelectedMediaItems(
        selectedItems,
        MediaTrackingEventName.AddedToLibrary,
      );

      bulkActionsApiService
        .addToLibraryAsync(currentCuids)
        .then((response) => {
          dispatch(
            showActionSuccessToast(
              response,
              'Added $count items to your library',
            ),
          );
        })
        .catch((error) => {
          showActionErrorDialog(error);
        });
    };
  }

  function addToCollection(collectionIds: string[]) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_ADD_TO_COLLECTION,
      );

      dispatch(closeConfirmModal());

      const selectedItems = getState().bulkActions.selectedItems;
      const currentCuids = selectedItems.map((item) => item.cuid);
      dispatch(clearItems());

      trackBulkSelectedMediaItems(
        selectedItems,
        MediaTrackingEventName.AddedToCollection,
      );

      bulkActionsApiService
        .addToCollectionsAsync(currentCuids, collectionIds)
        .then((response) => {
          dispatch(
            showActionSuccessToast(
              response,
              `Added $count items to your Collection`,
            ),
          );
        })
        .catch((error) => {
          showActionErrorDialog(error);
        });
    };
  }

  function removeFromCollection() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_REMOVE_FROM_COLLECTION,
      );

      dispatch(closeConfirmModal());

      const currentCuids = getState().bulkActions.selectedItems.map(
        (item) => item.cuid,
      );
      const collectionId = getState().bulkActions?.currentCollection?.id;
      const collectionName = getState().bulkActions?.currentCollection?.name;

      if (!collectionId) {
        showActionErrorDialog(
          'Nuts! We encountered an unexpected error, ' +
            'and were not able to remove these items from your Collection.',
        );
        return;
      }

      dispatch(clearItems());

      bulkActionsApiService
        .removeFromCollectionAsync(currentCuids, collectionId)
        .then((response) => {
          $rootScope.$broadcast('uia:remove-items-from-collection', {
            items: currentCuids,
          });
          queryClient.invalidateQueries({
            queryKey: [
              QueryKeyConstants.MediaFilters,
              QueryKeyConstants.MediaFiltersMediaItems,
            ],
          });

          dispatch(
            showActionSuccessToast(
              response,
              `Removed $count items from the ${collectionName} Collection`,
            ),
          );
        })
        .catch((error) => {
          showActionErrorDialog(error);
        });
    };
  }

  function removeFromConnect() {
    return async (dispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_REMOVE_FROM_CONNECT,
      );

      dispatch(closeConfirmModal());

      const sharedItemsEligibleToRemove =
        getSharedItemsEligibleToRemove(getState());
      const eligibleSelectedCuids = sharedItemsEligibleToRemove.map((item) => {
        return PartialCuid.buildFromNetworkItem(item.networkItem);
      });
      const selectedItems = getState().bulkActions.selectedItems.filter(
        (item) => eligibleSelectedCuids.includes(item.cuid),
      );

      dispatch(clearItems());

      trackBulkSelectedMediaItems(
        selectedItems,
        MediaTrackingEventName.RemovedFromConnect,
      );

      try {
        await dispatch(
          shareDataActions.removeSharedMedia(eligibleSelectedCuids),
        );

        dispatch(
          showActionSuccessToast(
            { cuids: eligibleSelectedCuids },
            `Removed $count items from Connect`,
          ),
        );
      } catch (error) {
        showActionErrorDialog(
          'Nuts! We encountered an unexpected error, ' +
            'and were not able to remove these items from Connect.' +
            error,
        );
      }
    };
  }

  function removeFromSelectedGallery() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_REMOVE_FROM_GALLERY,
      );

      dispatch(closeConfirmModal());

      const selectedGalleryId = getState().galleries.picker.selectedGalleryId;
      if (!selectedGalleryId) {
        showActionErrorDialog(
          'Nuts! We encountered an unexpected error, ' +
            'and were not able to remove these items from your Gallery.',
        );
        return;
      }

      const selectedGallery = Normalized.getById(
        getState().galleries.data.galleries,
        selectedGalleryId,
      )!;

      const currentCuids = getState().bulkActions.selectedItems.map(
        (item) => item.cuid,
      );
      dispatch(clearItems());

      return $q
        .all(
          currentCuids.map((cuid) => {
            const partialCuid: PartialCuid = cuid;
            return fanreelsApi.rejectItemFromFanreel(
              partialCuid,
              selectedGallery.reelId,
            );
          }),
        )
        .then(() => {
          const itemIds = currentCuids.map(
            (cuid) => PartialCuid.parse(cuid).itemId,
          );
          dispatch(galleriesDataActions.removeGalleryItems(itemIds));
          dispatch(
            showActionSuccessToast(
              // TODO: Determine which of these cuids were actually successfully removed?
              { cuids: currentCuids },
              `Removed $count items from '${selectedGallery.displayName}'`,
            ),
          );
        })
        .catch((error) => {
          showActionErrorDialog(error);
        });
    };
  }

  function removeFromGoogleAssetFeed() {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_REMOVE_FROM_GOOGLE_ASSET_FEED,
      );

      dispatch(closeConfirmModal());

      // The cuid field on the ISelectedItem interface is actually a partial
      // cuid, so we need to construct a real cuid using that value and
      // the session's clientId
      const clientId = ClientId(parseInt(getState().session.client.id, 10));
      const currentPartialCuids = getState().bulkActions.selectedItems.map(
        (item) => item.cuid,
      );
      const currentCuids = currentPartialCuids.map((pcuid) =>
        Cuid.fromPartial(pcuid, clientId),
      );

      const totalCount = await dispatch(
        taggingFeedActions.removeItems(currentCuids),
      );
      // Only clear the selected items if the action was successful
      if (totalCount) {
        dispatch(clearItems());
      }
    };
  }

  function publishToGoogleAssetFeed() {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_PUBLISH_TO_GOOGLE_ASSET_FEED,
      );

      dispatch(closeConfirmModal());

      // The cuid field on the ISelectedItem interface is actually a partial
      // cuid, so we need to construct a real cuid using that value and
      // the session's clientId
      const clientId = ClientId(parseInt(getState().session.client.id, 10));
      const currentPartialCuids = getState().bulkActions.selectedItems.map(
        (item) => item.cuid,
      );
      const currentCuids = currentPartialCuids.map((pcuid) =>
        Cuid.fromPartial(pcuid, clientId),
      );

      const totalCount = await dispatch(
        taggingFeedActions.publishItems(currentCuids),
      );
      // Only clear the selected items if the action was successful
      if (totalCount) {
        dispatch(clearItems());
      }
    };
  }

  function requestReviewNow() {
    return async (dispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_REQUEST_REVIEW_NOW,
      );

      dispatch(closeConfirmModal());

      const sharedItemsEligibleToRequestReviewNow =
        getSharedItemsEligibleToRequestReviewNow(getState());
      const elligibleSelectedCuids = sharedItemsEligibleToRequestReviewNow.map(
        (item) => {
          return PartialCuid(
            `${item.networkItem.network}.${item.networkItem.id}`,
          );
        },
      );
      const selectedItems = getState().bulkActions.selectedItems.filter(
        (item) => elligibleSelectedCuids.includes(item.cuid),
      );

      dispatch(clearItems());

      trackBulkSelectedMediaItems(
        selectedItems,
        MediaTrackingEventName.RequestedReview,
      );

      try {
        await dispatch(
          shareDataActions.shareMedia(
            elligibleSelectedCuids,
            /* forceRequestReview */ true,
          ),
        );
        await dispatch(shareDataActions.getSharedMediaAndStats());

        dispatch(
          showActionSuccessToast(
            { cuids: elligibleSelectedCuids },
            `Requested review for $count items.`,
          ),
        );
      } catch (error) {
        showActionErrorDialog(
          'Nuts! We encountered an unexpected error, ' +
            'and were not able to request review for these items from Connect.' +
            error,
        );
      }
    };
  }

  function addLabels(labels: string[]) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_ADD_LABELS,
      );

      dispatch(closeConfirmModal());

      const selectedItems = getState().bulkActions.selectedItems;
      const currentCuids = selectedItems.map((item) => item.cuid);
      const addedLabels = _(labels).unique();

      dispatch(clearItems());

      trackBulkSelectedMediaItems(
        selectedItems,
        MediaTrackingEventName.LabelAdded,
      );

      bulkActionsApi
        .updateLabels(currentCuids, { labelsToAdd: addedLabels })
        .then((response) => {
          queryClient.invalidateQueries({
            queryKey: [
              QueryKeyConstants.MediaFilters,
              QueryKeyConstants.MediaFiltersLabels,
            ],
          });
          dispatch(
            showActionSuccessToast(
              response,
              `Added ${addedLabels.length} labels to $count items`,
            ),
          );
        })
        .catch((error) => {
          showActionErrorDialog(error);
        });
    };
  }

  function removeLabels(labels: string[]) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_REMOVE_LABELS,
      );

      dispatch(closeConfirmModal());

      const selectedItems = getState().bulkActions.selectedItems;
      const currentCuids = selectedItems.map((item) => item.cuid);
      const removedLabels = _(labels).unique();

      dispatch(clearItems());

      trackBulkSelectedMediaItems(
        selectedItems,
        MediaTrackingEventName.LabelRemoved,
      );

      bulkActionsApi
        .updateLabels(currentCuids, { labelsToRemove: removedLabels })
        .then((response) => {
          queryClient.invalidateQueries({
            queryKey: [
              QueryKeyConstants.MediaFilters,
              QueryKeyConstants.MediaFiltersLabels,
            ],
          });
          dispatch(
            showActionSuccessToast(
              response,
              `Removed ${removedLabels.length} labels from $count items`,
            ),
          );
        })
        .catch((error) => {
          showActionErrorDialog(error);
        });
    };
  }

  function download() {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const uiaState = getState().bulkActions;

      if (computeIsDownloadDisabled(uiaState)) {
        return;
      }

      gaApi.recordEvent(
        gaApi.CATEGORY_BULK_UIA,
        gaApi.ACTION_BULK_UIA_DOWNLOAD,
      );

      const selectedItems = getState().bulkActions.selectedItems;
      const currentCuids = selectedItems.map((item) => item.cuid);

      const downloadWindow = $window.open('', '_blank');
      downloadWindow.document.write('<div>Fetching download URL...</div>');

      dispatch(clearItems());

      trackBulkSelectedMediaItems(
        selectedItems,
        MediaTrackingEventName.ItemDownloaded,
      );

      bulkActionsApiService
        .registerDownloadAsync(currentCuids)
        .then((response) => {
          const downloadToken = response.downloadToken;
          const downloadUrl =
            bulkActionsApiService.getRegisteredDownloadUrl(downloadToken);

          downloadWindow.location.href = downloadUrl;

          $timeout(() => {
            downloadWindow.close();
          }, 1000);
        });
    };
  }

  function openActionLabelsModal(addLabelsModal: boolean) {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(closeConfirmModal());

      const seletedItems = getState().bulkActions.selectedItems;

      const partialCuids = seletedItems.map((item) => item.cuid);
      const contentItems = await getContentLibraryItems({
        itemIds: partialCuids,
      });

      dispatch(setItemsBulkAddLabelsModal(contentItems.contentItems));
      if (addLabelsModal) {
        dispatch(openBulkAddLabelsModal());
      } else {
        dispatch(openBulkRemoveLabelsModal());
      }
    };
  }

  function openAddLabelsModal() {
    return openActionLabelsModal(true);
  }

  function openRemoveLabelsModal() {
    return openActionLabelsModal(false);
  }

  function closeAddLabelsModal() {
    return closeBulkAddLabelsModal();
  }

  function openConfirmationModal(action: string) {
    return openConfirmModal(action);
  }

  function closeConfirmationModal() {
    return closeConfirmModal();
  }

  function enableBulkAction(action: string) {
    return enableAction(action);
  }

  function disableBulkAction(action: string) {
    return disableAction(action);
  }

  function setCurrentCollection(id: string, name: string) {
    return setCollection(id, name);
  }

  function clearCurrentCollection() {
    return clearCollection();
  }

  function isDownloadDisabled() {
    return (dispatch, getState: () => ICuralateStore) => {
      const uiaState = getState().bulkActions;
      return computeIsDownloadDisabled(uiaState);
    };
  }

  function selectItems(params: {
    cuid: PartialCuid;
    currentItemIndex: number;
    hideRangeSelectToast?: (showAgainInDays: number) => void;
    isShiftPressed: boolean;
    lastSelectedItem: IRangeSelectedItem | null;
    orderedPartialCuids: PartialCuid[];
    setLastSelectedItem: (item: IRangeSelectedItem | null) => void;
    source?: MediaTrackingSource;
    wasRangeSelectToastShownToday?: boolean;
  }) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const {
        cuid,
        currentItemIndex,
        hideRangeSelectToast,
        isShiftPressed,
        lastSelectedItem,
        orderedPartialCuids,
        setLastSelectedItem,
        source,
        wasRangeSelectToastShownToday,
      } = params;

      const isFirstSelectedItem =
        getState().bulkActions.selectedItems.length === 0;
      const isInVirtualizedGrid = orderedPartialCuids.length !== 0;

      const shouldRangeSelect = isShiftPressed && lastSelectedItem;
      const shouldDisplayRangeSelectToast =
        isInVirtualizedGrid &&
        isFirstSelectedItem &&
        !wasRangeSelectToastShownToday &&
        hideRangeSelectToast;

      if (shouldRangeSelect) {
        // If user is holding shift and there was a previously selected item, select a range
        gaApi.recordEvent(
          gaApi.CATEGORY_BULK_UIA,
          gaApi.ACTION_BULK_UIA_RANGE_SELECT,
        );

        debouncedRightsStatuses(dispatch);

        const itemsToSelect = getItemsToTakeActionOn(
          currentItemIndex,
          orderedPartialCuids,
          lastSelectedItem!,
        );
        dispatch(selectCuidItems(itemsToSelect));
      } else if (shouldDisplayRangeSelectToast) {
        // If this is the first item being selected today and range select is enabled, show a helper toast
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Hold ⇧Shift and click to select many items at once',
            options: {
              toastTemplate: ALTERNATE_TOASTS.INFO_TOAST,
            },
          }),
        );

        if (isDefined(hideRangeSelectToast)) {
          hideRangeSelectToast(1);
        }

        dispatch(selectItem(cuid, () => null, source));
      } else {
        // Otherwise, just select the single item
        dispatch(selectItem(cuid, () => null, source));
      }

      const currentItem = {
        cuid,
        index: currentItemIndex,
      };

      setLastSelectedItem(currentItem);
    };
  }

  function unselectItems(params: {
    cuid: PartialCuid;
    currentItemIndex: number;
    isShiftPressed: boolean;
    lastSelectedItem: IRangeSelectedItem | null;
    orderedPartialCuids: PartialCuid[];
    setLastSelectedItem: (item: IRangeSelectedItem | null) => void;
  }) {
    return (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const {
        cuid,
        currentItemIndex,
        isShiftPressed,
        lastSelectedItem,
        orderedPartialCuids,
        setLastSelectedItem,
      } = params;

      if (isShiftPressed && lastSelectedItem) {
        // If user is holding shift and there was a previously selected item, unselect a range
        gaApi.recordEvent(
          gaApi.CATEGORY_BULK_UIA,
          gaApi.ACTION_BULK_UIA_RANGE_DESELECT,
        );

        const itemsToUnselect = getItemsToTakeActionOn(
          currentItemIndex,
          orderedPartialCuids,
          lastSelectedItem,
        );
        const cuidsToUnselect = itemsToUnselect.map((item) => item.cuid);
        dispatch(unselectCuidItems(cuidsToUnselect));
      } else {
        // Otherwise, just unselect the single item
        dispatch(unselectItem(cuid.toString()));
      }

      setLastSelectedItem(null);
    };
  }

  function getItemsToTakeActionOn(
    currentItemIndex: number,
    orderedPartialCuids: PartialCuid[],
    lastSelectedItem: IRangeSelectedItem,
  ): ISelectedItem[] {
    const lastSelectedIndex = lastSelectedItem.index;
    const isCurrentIndexAfterLastIndex = currentItemIndex > lastSelectedIndex;

    const cuidsToSelect = isCurrentIndexAfterLastIndex
      ? orderedPartialCuids.slice(lastSelectedIndex, currentItemIndex + 1)
      : orderedPartialCuids.slice(currentItemIndex, lastSelectedIndex + 1);

    return cuidsToSelect.map((partialCuid) => {
      return {
        cuid: partialCuid,
        onAddToGalleryCallback: () => null,
      };
    });
  }

  function selectItem(
    cuid: PartialCuid,
    onAddToGalleryCallback: () => any,
    source?: MediaTrackingSource,
  ) {
    return (dispatch: ReduxDispatch) => {
      gaApi.recordEvent(gaApi.CATEGORY_BULK_UIA, gaApi.ACTION_BULK_UIA_SELECT);

      debouncedRightsStatuses(dispatch);

      const item: ISelectedItem = {
        cuid,
        onAddToGalleryCallback,
        source,
      };

      return dispatch(selectCuidItem(item));
    };
  }

  function unselectItem(cuid: string) {
    gaApi.recordEvent(gaApi.CATEGORY_BULK_UIA, gaApi.ACTION_BULK_UIA_DESELECT);

    return unselectCuidItem(cuid);
  }

  function clearSelectedItems() {
    return clearItems();
  }

  function showActionSuccessToast(response, message: string) {
    return (dispatch: ReduxDispatch) => {
      const successfulItemsCount = response?.cuids?.length || 0;
      const successMessage = message.replace('$count', successfulItemsCount);

      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Success!',
          message: successMessage,
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    };
  }

  const fetchCaptionStatus = async (
    selectedVideosForCaptions: any,
    captionValue: string,
    dispatch: ReduxDispatch,
  ) => {
    const maxRetries = 10;
    const retryDelay = 8000;
    let retries = 0;

    while (retries < maxRetries) {
      try {
        const resdata = await getCaptionStatuses(selectedVideosForCaptions);
        const captionStatusesByCuids = resdata.data.captionStatusesByCuids;
        const invalidCuids = resdata.data.invalidCuids;
        if (invalidCuids.length) {
          dispatch(
            toastNotificationsActions.addToastNotification(
              captionInvalidCuidToast(captionValue),
            ),
          );
          return 'failed';
        }

        const inProgressKeys = Object.keys(captionStatusesByCuids).filter(
          (key) =>
            captionStatusesByCuids[key] === CAPTIONS_STATUS_VALUE.IN_PROGRESS,
        );
        const failedKeys = Object.keys(captionStatusesByCuids).filter(
          (key) => captionStatusesByCuids[key] === CAPTIONS_STATUS_VALUE.FAILED,
        );

        if (inProgressKeys.length) {
          dispatch(receiveSelectedItemsForCaptions(captionStatusesByCuids));
          retries++;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        } else if (failedKeys.length && !inProgressKeys.length) {
          dispatch(receiveSelectedItemsForCaptions(captionStatusesByCuids));

          dispatch(
            toastNotificationsActions.addToastNotification(
              captionFailedToast(captionValue),
            ),
          );
          return 'failed';
        } else if (!inProgressKeys.length && !inProgressKeys.length) {
          dispatch(receiveSelectedItemsForCaptions(captionStatusesByCuids));
          dispatch(
            toastNotificationsActions.addToastNotification(
              captionSuccessToast(captionValue),
            ),
          );
          return 'success';
        } else {
          retries++;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      } catch (error) {
        retries++;
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
      }
    }

    dispatch(
      toastNotificationsActions.addToastNotification(
        captionMaxTryFailedToast(captionValue),
      ),
    );

    return 'unknown';
  };

  function captionTurnOnOff(shouldCaption: boolean) {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      dispatch(setIsVideoActionInProgress(true));
      dispatch(clearSelectedItemsForCaptions());
      const { selectedItems } = getState().bulkActions;
      let cuids = selectedItems.map((obj: { cuid: PartialCuid }) => {
        return obj.cuid;
      });

      const captionValue = shouldCaption ? 'on' : 'off';
      const selectedVideosForCaptions = await getItemsMetadata(cuids).then(
        (metaItem) => {
          return metaItem
            .filter((item) => {
              if (shouldCaption) {
                return isDefined(item.videoSource) && !item?.isVideoCaptioned;
              } else {
                return (
                  isDefined(item.videoSource) &&
                  isDefined(item.isVideoCaptioned) &&
                  item?.isVideoCaptioned
                );
              }
            })
            .map((item) => {
              return Cuid.toPartial(item.rootCuid);
            });
        },
      );

      if (!selectedVideosForCaptions.length) {
        dispatch(clearItems());
        dispatch(setIsVideoActionInProgress(false));
        dispatch(
          toastNotificationsActions.addToastNotification(
            imagesSelectionFailedToast,
          ),
        );
        return;
      }

      if (selectedVideosForCaptions.length > 10) {
        dispatch(setIsVideoActionInProgress(false));
        dispatch(
          toastNotificationsActions.addToastNotification(
            cpationSelectionLimitToast,
          ),
        );
        return;
      }

      if (selectedVideosForCaptions.length > 0) {
        dispatch(
          toastNotificationsActions.addToastNotification(
            captionBulkRequestToast,
          ),
        );

        try {
          processCaptioningOnSelectedItems(
            shouldCaption,
            selectedVideosForCaptions,
          ).then(() => {
            dispatch(setCaptionTurnOnOffState(captionValue));
            const captionStatus = fetchCaptionStatus(
              selectedVideosForCaptions,
              captionValue,
              dispatch,
            );
            captionStatus.then((res) => {
              selectedVideosForCaptions.forEach((partialCuid) => {
                dispatch(updateItemOverlayDetails(partialCuid, true));
              });
            });

            dispatch(setIsVideoActionInProgress(false));
            dispatch(clearItems());
          });
        } catch (err: any) {
          dispatch(clearItems());
          dispatch(setIsVideoActionInProgress(false));
          dispatch(
            toastNotificationsActions.addToastNotification(
              captionFailedToast(captionValue),
            ),
          );
        }
      }
    };
  }

  function showActionErrorDialog(error) {
    $log.error('Bulk UIA: Action failed', error);
    ErrorNotificationService.showDefaultErrorDialog(
      'We were unable to complete the requested action.',
    );
  }

  function updateItemOverlayDetails(
    partialCuid: PartialCuid,
    forceRefresh?: boolean,
  ) {
    return () =>
      contentLibraryItemDetailsService.requestItemDetails(
        partialCuid,
        forceRefresh,
      );
  }

  const debouncedRightsStatuses: (dispatch: ReduxDispatch) => void = debounce(
    async (dispatch: ReduxDispatch) => {
      return dispatch(requestRightsStatuses());
    },
    300,
  );

  function requestRightsStatuses() {
    return async (dispatch: ReduxDispatch, getState: () => ICuralateStore) => {
      const { selectedItems, rightsRequestStatuses } = getState().bulkActions;
      const partialCuids = selectedItems.map(
        (selectedItem) => selectedItem.cuid,
      );
      const partialCuidsWithoutRights = partialCuids.filter(
        (partialCuid) => !rightsRequestStatuses[partialCuid],
      );

      if (partialCuidsWithoutRights.length > 0) {
        const { data } = await getRightsRequestStatuses(
          partialCuidsWithoutRights,
        );
        return dispatch(receiveRightsStatuses(data));
      }
    };
  }

  return {
    addLabels,
    addToCollection,
    addToLibrary,
    clearCurrentCollection,
    clearSelectedItems,
    closeAddLabelsModal,
    closeConfirmationModal,
    download,
    disableBulkAction,
    enableBulkAction,
    isDownloadDisabled,
    loadGlobalData,
    openAddLabelsModal,
    openConfirmationModal,
    openRemoveLabelsModal,
    openUia,
    removeFromCollection,
    removeFromConnect,
    removeFromSelectedGallery,
    removeFromGoogleAssetFeed,
    publishToGoogleAssetFeed,
    removeFromLibrary,
    removeLabels,
    requestReviewNow,
    selectItem,
    setCurrentCollection,
    setItemDetails,
    trackBulkSelectedMediaItems,
    unselectItem,
    updateItemOverlayDetails,
    requestRightsStatuses,
    selectItems,
    unselectItems,
    captionTurnOnOff,
    setSelectedItemsForContentSource,
  };
}

angular
  .module('curalate')
  .service('BulkActionsActions', [
    '$log',
    '$q',
    '$rootScope',
    '$timeout',
    '$window',
    'BulkActionsApiService',
    'ContentLibraryItemDetailsService',
    'ErrorNotificationService',
    'UniversalImageActionsService',
    'UniversalImageActionsApiService',
    'UniversalImageActionsView',
    BulkActionsActions,
  ]);
